import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import LazyLoad from "react-lazy-load";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { BsArrowRight, BsDownload } from "react-icons/bs";
import FileSaver from "file-saver";
import Box from "@mui/material/Box";
import ImageZoom from "react-image-zooom";
import "./homepageModal.css";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
}));

const BootstrapDialogTitle = ({ children, onClose, ...other }) => (
  <DialogTitle sx={{ m: 0, p: 2, color: "#0f2137" }} {...other}>
    {children}
    {onClose && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
  </DialogTitle>
);

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const HomepageModal = ({ Iindex, iTitle, iDocPath, iZoom }) => {
  const [open, setOpen] = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const handleClose = () => setOpen(false);

  const handleDownload = () => {
    if (iDocPath) FileSaver.saveAs(iDocPath, `${iTitle || "download"}.jpg`);
  };

  // ✅ Preload image to know when it's ready
  useEffect(() => {
    if (!iDocPath) return;
    const img = new Image();
    img.src = iDocPath;
    img.onload = () => setImgLoaded(true);
    img.onerror = () => setImgError(true);
  }, [iDocPath]);

  return (
    <BootstrapDialog
      style={{ zIndex: 999 - Iindex }}
      onClose={handleClose}
      scroll="paper"
      TransitionComponent={Transition}
      open={open}
    >
      <DialogContent dividers>
        <Box
          sx={{
            position: "relative",
            minHeight: "250px",
            width: "100%",
            overflow: "hidden",
            borderRadius: "8px",
            // ✅ Add these two lines ↓↓↓
            aspectRatio: "10/9", // or "4/3" / "1/1" depending on your image shape
            backgroundColor: "#f3f3f3", // subtle gray while loading
          }}
        >
          {/* ✅ Blurred background while loading */}
          {!imgLoaded && !imgError && (
            <div
              className="blur-bg"
              style={{
                backgroundImage: `url(${iDocPath})`,
                filter: "blur(20px)",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "absolute",
                inset: 0,
                transform: "scale(1.1)",
                transition: "opacity 0.4s ease-in-out",
                opacity: imgLoaded ? 0 : 1,
              }}
            />
          )}

          {/* Skip Button */}
          <a
            className="HomepageModalCloseButtonContainer"
            onClick={handleClose}
            style={{
              position: "absolute",
              top: 6,
              right: 8,
              zIndex: 999,
              display: "flex",
              alignItems: "center",
              gap: "3px",
              cursor: "pointer",
            }}
          >
            <p>Skip</p>
            <BsArrowRight
              style={{ color: "#fff", fontSize: "16px", strokeWidth: 0.5 }}
            />
          </a>

          {/* Download Button */}
          <a
            className="HomepageModalKnowButtonContainer"
            onClick={handleDownload}
            style={{
              position: "absolute",
              bottom: 6,
              right: 8,
              zIndex: 999,
              cursor: "pointer",
            }}
          >
            <BsDownload
              style={{ color: "#fff", fontSize: "18px", strokeWidth: 0.5 }}
            />
          </a>

          {/* ✅ Full image with smooth fade-in */}
          {!imgError && (
            <>
              {iZoom === "100" ? (
                <LazyLoad>
                  <img
                    className="HomePageModalImage"
                    src={iDocPath}
                    alt={iTitle}
                    loading="lazy"
                    onLoad={() => setImgLoaded(true)}
                    onError={() => setImgError(true)}
                    style={{
                      opacity: imgLoaded ? 1 : 0,
                      transition: "opacity 0.5s ease-in-out",
                      width: "100%",
                      height: "auto",
                      borderRadius: "8px",
                      objectFit: "contain",
                      display: "block",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                </LazyLoad>
              ) : (
                <ImageZoom
                  className="HomePageModalImage"
                  src={iDocPath}
                  alt={iTitle}
                  zoom={iZoom}
                />
              )}
            </>
          )}

          {/* ❌ Error fallback */}
          {imgError && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "#999",
                fontSize: "14px",
                background: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              ⚠️ Image failed to load.
            </div>
          )}
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default HomepageModal;
